import { nanoid } from 'nanoid';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { getFormValues, reduxForm } from 'redux-form';

import type { RootState } from 'af-reducers';

import { EMPLOYEE_EDIT } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import SubmitButton from 'af-components/SubmitButton';

import * as EmployeeActions from 'af-actions/employee';

import EditEmployeeForm from './EmployeeForm';
import type { CredentialFM, CredentialFormValues, CredentialRHFRow } from './formModel';
import { EmployeeFM } from './formModel';
import { convertStatusId } from './helpers';
import { validate } from './validation';
import ResourcePreviewNew from '../../Shared/ResourcePreviewNew';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<EmployeeFM>;

const Edit: React.FC<Props> = (props) => {
	const {
		findById,
		update,
		history,
		companyName,
		initialize,
		match: { params: { id } },
		location: { state: { orgAlias } },
		invalid,
		submitting,
		handleSubmit,
	} = props;

	const [title, setTitle] = React.useState<Nullable<string>>(null);

	const makeInitialRows = React.useCallback((rows: CredentialFM[]): CredentialRHFRow[] => {
		return (rows ?? []).map((r) => ({
			...r,
			rhfId: nanoid(UNIQUE_ID_SIZE),
		}));
	}, []);

	const {
		control,
		getValues,
		unregister,
		setError,
		clearErrors,
		formState,
		reset,
		setValue,
	} = useForm<CredentialFormValues>({
		mode: 'onChange',
	});

	// On mount
	React.useEffect(() => {
		const _initialize = async () => {
			const employee = await findById(+id);

			if (!employee) {
				throw new Error('Employee not found');
			}

			setTitle(employee.fullName);
			const form = EmployeeFM.fromVM(employee);
			initialize(form);
			reset({
				credentials: makeInitialRows(form.credentials ?? []),
			});
		};
		_initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const editEmployee = React.useCallback(async (form: EmployeeFM) => {
		form.employeeStatusId = convertStatusId(form.employeeStatusId);
		await update(EmployeeFM.toRM(form));
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(id, orgAlias, companyName));
	}, [companyName, history, id, orgAlias, update]);

	const breadcrumbs = React.useMemo(() => {
		return [
			{ label: 'Labor', url: CLIENT.COMPANY.RESOURCES.EMPLOYEE.LIST(orgAlias, companyName) },
			{ label: 'Preview', url: CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(id, orgAlias, companyName) },
			{ label: 'Edit' },
		];
	}, [companyName, id, orgAlias]);

	const goBack = React.useCallback(() => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(id, orgAlias, companyName));
	}, [companyName, history, id, orgAlias]);

	const renderBody = React.useCallback(() => {
		return (
			<EditEmployeeForm
				{...props}
				clearErrors={clearErrors}
				companyName={companyName}
				control={control}
				getValues={getValues}
				onSubmit={editEmployee}
				setError={setError}
				setValue={setValue}
				unregister={unregister}
			/>
		);
	}, [clearErrors, companyName, control, editEmployee, getValues, props, setError, setValue, unregister]);

	const renderHeaderBtns = React.useCallback(() => {
		return (
			<>
				<Button
					label="Back"
					onClick={goBack}
					style="secondary"
					type="button"
				/>
				<SubmitButton
					disabled={invalid || !formState.isValid}
					onClick={handleSubmit(editEmployee)}
					reduxFormSubmitting={submitting}
				/>
			</>
		);
	}, [editEmployee, formState.isValid, goBack, handleSubmit, invalid, submitting]);

	if (!title) {
		return null;
	}

	return (
		<ResourcePreviewNew
			breadcrumbs={breadcrumbs}
			companyName={companyName}
			headerTitle={title}
			includeRelatedWorkOrders={false}
			renderBody={renderBody}
			renderHeaderButtons={renderHeaderBtns}
		/>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	const currentFormValues = getFormValues(EMPLOYEE_EDIT)(state) as EmployeeFM;

	return {
		companyName: companyData.name,
		credentials: currentFormValues?.credentials ?? [],
	};
}

function mapDispatchToProps() {
	return {
		findById: EmployeeActions.findById,
		update: EmployeeActions.update,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm({ form: EMPLOYEE_EDIT, validate })
);
export default enhance(Edit);
